import React, { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogWindow, DialogWindowClose, useDialogManager } from 'src/components/widgets/dialog';
import { backofficeUrl, T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import AutoAwesomeIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import InfoIcon from 'src/shared/material_icons/action/info.svg';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import styled from 'styled-components';

const NO_CREDITS_DIALOG_ID = 'ai-studio-no-credits';

// Same look as the other AI credit dialogs (serviceCreditsAiStudioModal): AI Agents Studio gradient header,
// info box and gradient call to action.
const AI_STUDIO_GRADIENT = 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)';

// Borderless window so the gradient header can bleed to the rounded top edge; the close icon is white over it.
const NoCreditsWindow = styled(DialogWindow)`
    padding: 0;
    width: 520px;
    flex-basis: 520px;
    max-width: 520px;
    border-radius: 14px;
    overflow: hidden;

    ${DialogWindowClose} {
        color: #ffffff;
        fill: #ffffff;
        right: 20px;
        top: 20px;
    }
`;

const Header = styled.div`
    background: ${AI_STUDIO_GRADIENT};
    color: #ffffff;
    padding: 22px 32px 20px;
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 20px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
        display: block;
    }
`;

const HeaderTitle = styled.h1`
    margin: 10px 0 0;
    font-size: 22px;
    font-weight: 700;
    color: #ffffff;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px 32px 14px;
    background: #ffffff;
`;

const InfoBox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border: 1px solid #ece7f7;
    border-radius: 10px;
    padding: 13px 16px;
`;

const InfoIconCircle = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.1);
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
    }
`;

const InfoText = styled.div`
    font-size: 13px;
    line-height: 1.45;
    color: #607d8b;
`;

const ActionArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 32px 20px;
    background: #ffffff;
    border-top: 1px solid #f1eef8;
`;

const UpgradeButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 14px 20px;
    background: ${AI_STUDIO_GRADIENT};
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease-in;

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
    }

    &:hover {
        opacity: 0.92;
    }
`;

export interface FreeAICreditsUsedUp {
    /** Why the AI tools are blocked: a trial or a Shopify development store with its free AI credits used up. */
    message: string;
    isShopifyDevStore: boolean;
    /** The Upgrade of the trial banner is available: a trial outside Shopify, with subscription permissions. */
    canUpgradeTrial: boolean;
    /** A trial outside Shopify whose user cannot manage the subscription: the owner has to upgrade. */
    needsSubscriptionOwner: boolean;
    /** Same behaviour of the trial banner (subscriptions.tsx, onClickSubscribe): the trial upgrade checkout. */
    upgradeTrial: () => void;
}

/**
 * NO_CREDITS with the AI credit model: there are no credit packs to buy. Every account gets a one-time amount of
 * free AI credits, and only a trial or a Shopify development store is stopped when they are used up (a paying seller
 * keeps using the tools and pays for them). The dialog and the AI assistant card use this same explanation.
 * A trial outside Shopify gets the same Upgrade of the trial banner in My Account > Subscription, which leads to
 * the checkout. The call to action of Shopify (trial, development store) will be added later.
 */
export function useFreeAICreditsUsedUp(): FreeAICreditsUsedUp {
    const { ecommerceName, shopifyEcommerceSettings, sellerMarketplace } = useSubscriptionData();
    const { isAllowed } = usePermissions();
    const navigate = useNavigate();

    const isShopify = ecommerceName === 'SHOPIFY';
    // Same rule as the backend: a Shopify development store in every environment.
    const isShopifyDevStore = isShopify && shopifyEcommerceSettings?.isTestStore === true;
    const canManageSubscription = isAllowed(['SUBSCRIPTION']);
    // Same conditions of the Upgrade button of the trial banner (subscriptions.tsx).
    const canUpgradeTrial = !isShopify && canManageSubscription && sellerMarketplace !== 'MICROSOFT_AZURE';

    return {
        message: isShopifyDevStore
            ? T._('Your development store does not have enough free AI credits for this operation. Switch to a paid account to continue using AI tools.', 'Admin')
            : T._('Your trial does not have enough free AI credits for this operation. Switch to a paid account to continue using AI tools.', 'Admin'),
        isShopifyDevStore,
        canUpgradeTrial,
        needsSubscriptionOwner: !isShopify && !canManageSubscription,
        upgradeTrial: () => {
            if (sellerMarketplace === 'HP') window.open(backofficeUrl('/Seller/UpgradeTrialSubscription'), '_blank');
            else navigate(backofficeUrl('/Seller/UpgradeTrialSubscription'));
        }
    };
}

interface AIStudioNoCreditsDialogProps {
    onClose: () => void;
    /** Dialog heading. Defaults to the generic out-of-credits copy; callers can override it to fit the context. */
    title?: string;
}

/** Shown when the backend reports NO_CREDITS: the free AI credits of a trial or a development store are used up. */
const AIStudioNoCreditsDialog: FC<AIStudioNoCreditsDialogProps> = ({ onClose, title }) => {
    const { message, isShopifyDevStore, canUpgradeTrial, upgradeTrial } = useFreeAICreditsUsedUp();

    const onUpgrade = () => {
        onClose();
        upgradeTrial();
    };

    return (
        <Dialog windowDecorator={NoCreditsWindow} onClose={onClose}>
            <Header>
                <Badge>
                    <AutoAwesomeIcon />
                    {T._('AI Agents Studio', 'Admin')}
                </Badge>
                <HeaderTitle>{title ?? T._('Free AI credits used up', 'Admin')}</HeaderTitle>
            </Header>
            <Body>
                <InfoBox>
                    <InfoIconCircle>
                        <InfoIcon />
                    </InfoIconCircle>
                    <InfoText>{message}</InfoText>
                </InfoBox>
            </Body>
            {!isShopifyDevStore && canUpgradeTrial && (
                <ActionArea>
                    <UpgradeButton type='button' onClick={onUpgrade}>
                        {T._('Upgrade', 'Admin')}
                        <ArrowForwardIcon />
                    </UpgradeButton>
                </ActionArea>
            )}
        </Dialog>
    );
};

/** Returns a stable callback that opens the AI Studio no-credits dialog with an optional custom title. */
export function useAIStudioCreditsDialog(): { showNoCreditsDialog: (title?: string) => void } {
    const { showDialog, closeDialog } = useDialogManager();

    const showNoCreditsDialog = useCallback(
        (title?: string) => {
            showDialog(NO_CREDITS_DIALOG_ID, <AIStudioNoCreditsDialog title={title} onClose={() => closeDialog(NO_CREDITS_DIALOG_ID)} />);
        },
        [showDialog, closeDialog]
    );

    return { showNoCreditsDialog };
}
